// The sign-in records (02-DESIGN/01-identity/02-the-auth-service.md § the
// audit records): placed, refused and ended, listed through the signins verb,
// on *Your activity*, which shows the person's own as the verb answers them.
// The workspace's are the observability solution's to show (hq decision 0048).
// The table is who, what and when; a row opens the record's page,
// `me/activity/<seq>` (RecordPage.tsx; 01-the-console.md § the moves, page by
// page: Your activity), with why it was refused, as which role, until when,
// what it sent. A member's page on People shows their recent sign-ins from
// the same verb, as a list whose rows hold the record's facts: there is no
// page for another person's record. A record without a sequence, from an
// older service, has no page, and its row does not open. An app or a workload a record names is
// a link to its page where the role is offered that page (§ conventions: a
// name is a link).

import { tenantPath } from "../../address/address.ts";
import { pageOffered } from "../../roles/offers.ts";

import {
  Avatar,
  Button,
  Empty,
  NotAllowed,
  Pending,
  Pill,
  since,
  when,
  useLoad,
  type Tone,
} from "../../ui/components.tsx";
import { Ident, NameLink } from "../../ui/ident.tsx";
import { Table, type Column } from "../../ui/table.tsx";
import {
  Subjects,
  type SigninsListReply,
  type SigninsListRequest,
  type SignInRecord,
} from "../../wire/auth.ts";
import { endingOf, Outcome, SERVICE } from "../../wire/identity.ts";
import { recordProvider } from "../../platform/names.ts";
import { usePage } from "../../platform/session.tsx";
import { useSurface } from "../surface.tsx";
import { recordId } from "./ids.ts";

/** Shown is one record on the page, as the verb listed it, with its id in the address where it has one. */
export interface Shown {
  /** The row's key in the table: the id, or the record's place in the listing where it has none. */
  key: string;
  /** The record's id in the address, its sequence; absent where the record carries none. */
  id?: string;
  record: SignInRecord;
}

/** REASONS puts the auth service's refusal codes in a person's words; an unlisted code is shown as written. */
const REASONS: Record<string, string> = {
  "no-row": "no access rule matched",
  "tenant-unknown": "no such workspace",
  "tenant-suspended": "the workspace is suspended",
  "trust-disabled": "that sign-in is switched off",
  expired: "the token had expired",
  "not-yet-valid": "the token was not valid yet",
  "wrong-issuer": "the token came from another provider",
  "wrong-audience": "the token was meant for another app",
  "wrong-organisation": "the account is in another organisation",
  "bad-signature": "the token's signature did not check out",
  "malformed-token": "the token could not be read",
  "provider-unreachable": "the provider could not be reached",
  "limiter-declined": "too many attempts",
  "app-inactive": "the app is disconnected",
};

export function reasonWords(code: string | undefined): string {
  if (code === undefined || code === "") {
    return "";
  }
  return REASONS[code] ?? code.replace(/-/g, " ");
}

export function whoOf(x: SignInRecord): string {
  if (x.principal !== undefined) {
    return x.principal.display ?? x.principal.subject;
  }
  if (x.app !== undefined) {
    return `app ${x.app}`;
  }
  if (x.workload !== undefined) {
    return `workload ${x.workload}`;
  }
  return "someone";
}

/** SignInRecords lists the records the signins verb answers, the caller's own where `mine` names them. */
export function SignInRecords({ mine }: { mine?: string }) {
  const s = useSurface();
  const { platform } = usePage();
  const [list, reload] = useLoad("signins", () =>
    s.bus.call<SigninsListReply>(SERVICE, Subjects.signinsList(), {
      limit: 200,
    } satisfies SigninsListRequest),
  );

  const all = (list.state === "ready" ? shownOf(list.value.records) : []).filter(
    (r) => mine === undefined || r.record.principal?.subject === mine,
  );

  return (
    <>
      {list.state === "pending" && <Pending what="Reading the sign-ins" />}
      {list.state === "refused" && <NotAllowed err={list.err} what="Sign-ins" />}
      {list.state === "ready" && all.length === 0 && (
        <Empty>No sign-ins recorded yet. Yours is the first, once its record lands.</Empty>
      )}
      {all.length > 0 && (
        <Table
          label="Sign-ins"
          rows={all}
          rowKey={(r) => r.key}
          sort={{ key: "when", dir: "desc" }}
          columns={columnsFor(platform.platform_provider)}
          to={(r) => (r.id === undefined ? undefined : tenantPath(s.tenant, "me/activity", r.id))}
        />
      )}
      <div className="row" style={{ marginTop: 12 }}>
        <Button size="sm" kind="quiet" onClick={reload}>
          Reload
        </Button>
      </div>
    </>
  );
}

/** shownOf names each record of a listing by its id, newest first. */
export function shownOf(records: readonly SignInRecord[]): Shown[] {
  return records
    .map((record, i): Shown => {
      const id = recordId(record);
      return id === undefined ? { key: `row-${String(i)}`, record } : { key: id, id, record };
    })
    .reverse();
}

/** whatOf is a record's outcome in a person's words. */
export function whatOf(x: SignInRecord): { word: string; tone: Tone } {
  if (x.outcome === Outcome.placed) {
    return { word: "signed in", tone: "live" };
  }
  if (x.outcome === Outcome.refused) {
    return { word: "not allowed", tone: "bad" };
  }
  if (x.outcome === Outcome.ended) {
    return {
      word: endingOf(x.end_reason) === "signed-out" ? "signed out" : "session ended",
      tone: "neutral",
    };
  }
  return { word: x.outcome, tone: "neutral" };
}

/** subOf is the one fact under who: the provider, and the connection's name. */
function subOf(x: SignInRecord, provider: string): string {
  const from =
    x.connection?.name !== undefined && x.connection.name !== "" ? `from ${x.connection.name}` : "";
  return [provider, from].filter((part) => part !== "").join(" · ");
}

/** detailText is the detail's words, which the filter matches though the table does not show them. */
function detailText(x: SignInRecord): string {
  if (x.outcome === Outcome.refused) {
    return reasonWords(x.reason);
  }
  if (x.outcome === Outcome.ended) {
    return endingOf(x.end_reason) === "expired" ? "the sign-in expired" : "";
  }
  if (x.outcome === Outcome.placed) {
    return x.role !== undefined && x.role !== "" ? `as ${x.role}` : "";
  }
  return "";
}

/** columnsFor are the table's columns — who, what, when — newest first, naming providers as the profile does. */
function columnsFor(platformProvider: Parameters<typeof recordProvider>[1]): Column<Shown>[] {
  const sub = (x: SignInRecord) => subOf(x, recordProvider(x, platformProvider));
  return [
    {
      key: "who",
      header: "Who",
      width: "minmax(0, 1.6fr)",
      value: (r) => whoOf(r.record),
      text: (r) => `${whoOf(r.record)} ${sub(r.record)} ${detailText(r.record)}`,
      cell: (r) => {
        const who = whoOf(r.record);
        const line = sub(r.record);
        return (
          <span className="with-avatar">
            <Avatar name={who} small />
            <span className="cell">
              <span className="cell-main">{who}</span>
              {line !== "" && <span className="cell-sub">{line}</span>}
            </span>
          </span>
        );
      },
    },
    {
      key: "what",
      header: "What",
      width: "140px",
      facet: "Status",
      value: (r) => whatOf(r.record).word,
      cell: (r) => {
        const w = whatOf(r.record);
        return <Pill tone={w.tone}>{w.word}</Pill>;
      },
    },
    {
      key: "when",
      header: "When",
      width: "150px",
      value: (r) => r.record.when,
      text: (r) => since(r.record.when) || when(r.record.when),
      cell: (r) => <span className="small muted">{since(r.record.when) || when(r.record.when)}</span>,
    },
  ];
}

/**
 * RecordDetail is one record's detail, on its page: each fact the record
 * holds, the rest left out. The app or the workload that signed in is a link
 * to its page where the role is offered it.
 */
export function RecordDetail({ x, provider }: { x: SignInRecord; provider: string }) {
  const s = useSurface();
  const ending = x.outcome === Outcome.ended ? endingOf(x.end_reason) : undefined;
  const w = whatOf(x);
  return (
    <dl className="kv">
      <dt>What</dt>
      <dd>
        <Pill tone={w.tone}>{w.word}</Pill>
      </dd>
      <dt>When</dt>
      <dd>
        {when(x.when)} <span className="muted">· {since(x.when)}</span>
      </dd>
      <dt>Signed in with</dt>
      <dd>{provider}</dd>
      {x.app !== undefined && x.app !== "" && (
        <>
          <dt>App</dt>
          <dd>
            <Ident
              value={x.app}
              label="the app's id"
              to={pageOffered(s.role, "apps") ? tenantPath(s.tenant, "apps", x.app) : undefined}
            />
          </dd>
        </>
      )}
      {x.workload !== undefined && x.workload !== "" && (
        <>
          <dt>Workload</dt>
          <dd>
            {pageOffered(s.role, "workloads") ? (
              <NameLink to={tenantPath(s.tenant, "workloads", x.workload)}>{x.workload}</NameLink>
            ) : (
              <span className="mono">{x.workload}</span>
            )}
          </dd>
        </>
      )}
      {x.connection?.name !== undefined && x.connection.name !== "" && (
        <>
          <dt>From</dt>
          <dd>{x.connection.name}</dd>
        </>
      )}
      {x.outcome === Outcome.refused && (
        <>
          <dt>Why not</dt>
          <dd>{reasonWords(x.reason)}</dd>
          {x.unverified === true && x.issuer !== undefined && x.issuer !== "" && (
            <>
              <dt>Issuer, as claimed</dt>
              <dd>
                <Ident value={x.issuer} label="the issuer" head={28} tail={6} />
              </dd>
            </>
          )}
        </>
      )}
      {x.outcome === Outcome.placed && x.role !== undefined && x.role !== "" && (
        <>
          <dt>As</dt>
          <dd>{x.role}</dd>
        </>
      )}
      {x.outcome === Outcome.placed && x.expires !== undefined && (
        <>
          <dt>Until</dt>
          <dd>{when(x.expires)}</dd>
        </>
      )}
      {ending === "expired" && (
        <>
          <dt>Why it ended</dt>
          <dd>the sign-in expired</dd>
        </>
      )}
      {x.outcome === Outcome.ended && x.sent !== undefined && x.received !== undefined && (
        <>
          <dt>Messages</dt>
          <dd>
            {x.sent.msgs} sent, {x.received.msgs} received
          </dd>
        </>
      )}
      {x.principal !== undefined && (
        <>
          <dt>Subject</dt>
          <dd>
            <Ident value={x.principal.subject} label="the subject" />
          </dd>
        </>
      )}
    </dl>
  );
}

/** RECENT is how many sign-ins a member's page lists. */
const RECENT = 5;

/** recentFacts is one recent sign-in's facts in a row: the provider, where from, as which role, or why not. */
export function recentFacts(x: SignInRecord, provider: string): string {
  const from =
    x.connection?.name !== undefined && x.connection.name !== "" ? `from ${x.connection.name}` : "";
  const detail =
    x.outcome === Outcome.refused
      ? reasonWords(x.reason)
      : x.outcome === Outcome.placed && x.role !== undefined && x.role !== ""
        ? `as ${x.role}`
        : x.outcome === Outcome.ended && endingOf(x.end_reason) === "expired"
          ? "the sign-in expired"
          : "";
  return [provider, from, detail].filter((part) => part !== "").join(" · ");
}

/**
 * RecentSignIns is one person's latest sign-ins, newest first, from the
 * signins verb — which answers an admin the workspace's — for a member's
 * page on People: a list, each row when it was, the record's facts and its
 * outcome. A row opens nothing: another person's record has no page.
 */
export function RecentSignIns({ subject }: { subject: string }) {
  const s = useSurface();
  const { platform } = usePage();
  const [list] = useLoad(`signins/of/${subject}`, () =>
    s.bus.call<SigninsListReply>(SERVICE, Subjects.signinsList(), {
      limit: 200,
    } satisfies SigninsListRequest),
  );
  if (list.state === "pending") {
    return <Pending what="Reading their sign-ins" />;
  }
  if (list.state === "refused") {
    return <NotAllowed err={list.err} what="Sign-ins" />;
  }
  const theirs = shownOf(list.value.records)
    .filter((r) => r.record.principal?.subject === subject)
    .slice(0, RECENT);
  if (theirs.length === 0) {
    return <p className="small faint">No sign-in recorded yet.</p>;
  }
  return (
    <div className="list">
      {theirs.map(({ key, record: x }) => {
        const w = whatOf(x);
        const facts = recentFacts(x, recordProvider(x, platform.platform_provider));
        return (
          <div className="li" key={key}>
            <span className="grow small">
              <span title={when(x.when)}>{since(x.when) || when(x.when)}</span>
              {facts !== "" && <span className="muted"> · {facts}</span>}
            </span>
            <Pill tone={w.tone}>{w.word}</Pill>
          </div>
        );
      })}
    </div>
  );
}
